import { createContext, ReactNode, useCallback, useContext } from 'react';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import {
  CurrentShow,
  useSafeCurrentShow,
} from '../../flows/showcase/functions/useCurrentShow/useCurrentShow';
import { showInfo } from '../../flows/showcase/functions/useCurrentShow/useCurrentShow.utils';
import type { ShowCasePageProps } from '../../flows/showcase/ShowCasePageContent';
import { currentUserId, currentUserId$ } from '../../functions/auth/utils';
import { firestore, serverNow } from '../../functions/firebase/firebaseAccess';
import {
  docQuery,
  memorize,
  WithID,
} from '../../functions/firebase/rxfireExtensions';
import { useObservable } from '../../functions/hooks/useObservable';
import { useOpenUserProfile } from '../../functions/hooks/useUserProfileHandler';
import { useRootNavigation } from '../../functions/navigation/PopNavigatorProvider';
import { useServiceFactory } from '../../functions/services';
import { SpecialUrlCode } from '../../functions/urlCodeHelpers';
import {
  CreateFollowerSource,
  CreateFollowerSourceTypeEnum,
} from '../../generated/api';
import { Profile, UpcomingShow } from '../../models/ClientModels';
import { shareReplay } from '../../utils/rxExtensions';

export const showCaseRow$ = new BehaviorSubject<CurrentShow[]>([]);

export type SellerProfileOpenScene = string;
export type GeneralUserProfileOpenScene = string;

export type UserProfileOpenScene =
  | SellerProfileOpenScene
  | GeneralUserProfileOpenScene;

export type FollowButtonScene = string;

export const api = {
  useIsFollowing(sellerId: string): boolean {
    return useObservable(
      isFollowing$(sellerId),
      `is-following-${sellerId}`,
      false,
    );
  },
  useAmIBlocked(show: CurrentShow) {
    return useObservable(
      currentUserId$.pipe(
        switchMap(
          (uid): Observable<boolean> =>
            uid == null
              ? of(false)
              : docQuery(showInfo(show).blockedUserCollectionRef(uid)).pipe(
                  map(d => d.exists),
                ),
        ),
        distinctUntilChanged(),
        shareReplay(1),
      ),
      `am-I-blocked-${showInfo(show).identifier}`,
      false,
    );
  },
  useFollowFn(): (user: { id: string }) => Promise<void> {
    const inShow = useSafeCurrentShow();
    const { userService } = useServiceFactory();

    return useCallback(
      async user => {
        const myId = currentUserId();

        if (myId == null || myId === user.id) return;

        let source: undefined | CreateFollowerSource;
        if (inShow) {
          const liveStreamId = showInfo(inShow).activeLiveStreamId;
          if (liveStreamId) {
            source = {
              type: CreateFollowerSourceTypeEnum.LiveStream,
              liveStreamId,
            };
          } else if (inShow.type === 'upcomingShow') {
            source = {
              type: CreateFollowerSourceTypeEnum.UpcomingShow,
              upcomingShowId: inShow.upcomingShow.id,
            };
          }
        }

        await userService.userApiControllerCreateFollower({
          userId: user.id,
          createFollowerDto: { source },
        });
      },
      [inShow, userService],
    );
  },
  useUnfollowFn(): (user: WithID<Profile>) => Promise<void> {
    const { userService } = useServiceFactory();

    return useCallback(
      async user => {
        const myId = currentUserId();
        if (myId == null) return;

        // users can not unfollow themselves, or the API will return an error
        if (myId === user.id) return;

        await userService.userApiControllerRemoveFollower({
          userId: user.id,
          followerId: myId,
        });
      },
      [userService],
    );
  },
  useIsInWatchList(upcomingShowId: string): boolean {
    return useObservable(
      isInWatchList(upcomingShowId),
      `is-upcoming-show-${upcomingShowId}-in-watchlist`,
      false,
    );
  },
  async addToWatchList(upcomingShow: WithID<UpcomingShow>) {
    const uid = currentUserId();
    if (uid == null) {
      return;
    }
    await firestore()
      .collection('privateProfiles')
      .doc(uid)
      .collection('watchLists')
      .doc(upcomingShow.id)
      .set({
        createdAt: serverNow,
        upcomingShow,
      });
  },
  async removeFromWatchList(upcomingShow: WithID<UpcomingShow>) {
    const uid = currentUserId();
    if (uid == null) {
      return;
    }
    await firestore()
      .collection('privateProfiles')
      .doc(uid)
      .collection('watchLists')
      .doc(upcomingShow.id)
      .delete();
  },
  useOpenShow() {
    const navigation = useRootNavigation();
    const openProfile = useOpenUserProfile();

    return useCallback(
      (
        show: CurrentShow,
        shows: CurrentShow[] = [],
        options?: Partial<ShowCasePageProps>,
      ) => {
        const navigateToShow = () => {
          // note(zhigang1992): temp disable gestures
          // https://www.notion.so/Disable-left-right-swipe-gesture-in-the-live-stream-room-801449dd3a034565bbd60688f8c36c18
          showCaseRow$.next(shows);
          navigation.push('showcase', {
            urlCode: showInfo(show).urlCode,
            ...options,
          });
        };
        if (show.type === 'event') {
          if (show.asCover) {
            navigation.push('showcase', {
              urlCode: SpecialUrlCode.event(show.event.id),
              showTicket: true,
            });
          } else {
            navigation.push('discoveryEvent', { eventId: show.event.id });
          }
          return;
        }
        if (show.type === 'seller' && !options?.reelOnly) {
          void openProfile({
            profile: show.seller,
          });
          return;
        }
        navigateToShow();
      },
      [navigation, openProfile],
    );
  },
};

type CommonAPI = typeof api;

export const CommonAPIProviderContext = createContext<CommonAPI>(null as any);
export const useCommonAPI = () => useContext(CommonAPIProviderContext);

export const isFollowing$ = memorize((sellerId: string) =>
  currentUserId$.pipe(
    switchMap(
      (uid): Observable<boolean> =>
        uid == null
          ? of(false)
          : uid === sellerId
            ? of(true)
            : docQuery(
                firestore()
                  .collection('profiles')
                  .doc(uid)
                  .collection('followings')
                  .doc(sellerId),
              ).pipe(map(d => d.exists)),
    ),
    distinctUntilChanged(),
    shareReplay(1),
  ),
);

const isInWatchList = memorize((upcomingShowId: string) =>
  currentUserId$.pipe(
    switchMap(
      (uid): Observable<boolean> =>
        uid == null
          ? of(false)
          : docQuery(
              firestore()
                .collection('privateProfiles')
                .doc(uid)
                .collection('watchLists')
                .doc(upcomingShowId),
            ).pipe(map(d => d.exists)),
    ),
    distinctUntilChanged(),
    shareReplay(1),
  ),
);

export const CommonAPIProvider = (props: { children: ReactNode }) => {
  return (
    <CommonAPIProviderContext.Provider value={api}>
      {props.children}
    </CommonAPIProviderContext.Provider>
  );
};
